import InitiativeApi from '@/api/Initiative'
import type {
  InitiativeFetchParams,
  InitiativeSearchCollectsParams,
  InitiativeSearchParams,
} from '@/api/Initiative/types.ts'
import type { FetchParams, ParamType } from '@/api/types.ts'
import { getNextPageParam } from '@/utils/pagination'
import { isSearchTermValid } from '@/utils/searchTerm'
import {
  partialMatchKey,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'

export const LAST_COLLECTS_REFETCH_INTERVAL = 60000

export function useFetchInitiative(
  params: InitiativeFetchParams,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['fetch-initiative', params],
    queryFn: () => InitiativeApi.fetch([], params),
    enabled,
  })
}

export function useFetchInitiativeInfinite(
  params: Omit<InitiativeFetchParams, 'page'> & { limit: number },
  enabled: boolean = true
) {
  return useInfiniteQuery({
    queryKey: ['fetch-initiative-infinite', params],
    queryFn: ({ pageParam }) =>
      InitiativeApi.fetch([], { ...params, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage, allPages, params.limit),
    enabled,
  })
}

export function getInitiativeQueryOptions(id: ParamType) {
  return queryOptions({
    queryKey: ['get-initiative', id],
    queryFn: () => InitiativeApi.get(id),
  })
}

export function useGetInitiative(id: ParamType) {
  return useQuery(getInitiativeQueryOptions(id))
}

export function useFetchInitiativeAdminUsers(
  id: ParamType,
  params: FetchParams
) {
  return useQuery({
    queryKey: ['fetch-initiative-permissions', id, params],
    queryFn: () => InitiativeApi.fetchAdminUsers([id], params),
  })
}

export function useSearchInitiative(
  initiativeId: ParamType,
  params: InitiativeSearchParams
) {
  return useQuery({
    queryKey: ['search-initiative', initiativeId, params],
    queryFn: () => InitiativeApi.search([initiativeId], params),
    enabled: !!params.search,
  })
}

export function useGetInitiativeSummary(initiativeId: ParamType) {
  return useQuery({
    queryKey: ['get-initiative-summary', initiativeId],
    queryFn: () => InitiativeApi.summary([initiativeId]),
  })
}

export function useFetchInitiativeLastCollects(initiativeId: ParamType) {
  return useQuery({
    queryKey: ['fetch-initiative-last-collects', initiativeId],
    queryFn: () => InitiativeApi.lastCollects([initiativeId]),
    refetchInterval: LAST_COLLECTS_REFETCH_INTERVAL,
  })
}

export function useSearchInitiativeCollects(
  initiativeId: ParamType,
  params: InitiativeSearchCollectsParams
) {
  return useInfiniteQuery({
    queryKey: ['fetch-initiative-search-collects', initiativeId, params],
    queryFn: ({ pageParam }) =>
      InitiativeApi.searchCollects([initiativeId], {
        ...params,
        page: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(lastPage, allPages, params.limit),
    enabled: isSearchTermValid(params.search),
  })
}

export function useSearchInitiativeFeatureCollects(
  initiativeId: ParamType,
  featureId: number | undefined,
  params: InitiativeSearchCollectsParams
) {
  const featureSearchKey = [
    'fetch-initiative-search-feature-collects',
    initiativeId,
    featureId,
  ]

  return useInfiniteQuery({
    queryKey: [...featureSearchKey, params],
    queryFn:
      featureId === undefined
        ? skipToken
        : ({ pageParam }) =>
            InitiativeApi.searchCollects([initiativeId], {
              ...params,
              featureId,
              page: pageParam,
            }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.page + 1 < lastPage.pages ? lastPage.page + 1 : undefined,
    placeholderData: (previousData, previousQuery) =>
      previousQuery && partialMatchKey(previousQuery.queryKey, featureSearchKey)
        ? previousData
        : undefined,
    enabled: isSearchTermValid(params.search),
  })
}
